import type { CSSProperties } from 'react';

export type LogoVariant = 'full' | 'mark' | 'wordmark';
export type LogoTone = 'brand' | 'light' | 'mono';

export interface LogoProps {
  size?: number;
  variant?: LogoVariant;
  tone?: LogoTone;
  /**
   * Public-root path prefix where the logo PNGs live.
   * Defaults to /destekgor/ (set up in each panel's public/ dir).
   */
  basePath?: string;
  style?: CSSProperties;
  className?: string;
}

/**
 * DestekGör brand logo.
 *
 * Source ratios — wordmark (logo-kare.png) 1504×357 ≈ 4.21:1, mark 1:1.
 * `size` controls glyph height; the wordmark scales proportionally.
 *
 * Tone:
 *   - brand (default) — natural-color PNG
 *   - light  — CSS filter inverts to white (use on dark backgrounds)
 *   - mono   — CSS filter renders as black silhouette
 */
export function Logo({
  size = 22,
  variant = 'full',
  tone = 'brand',
  basePath = '/destekgor/',
  style,
  className,
}: LogoProps) {
  const wordmarkH = size * 1.1;
  const wordmarkW = wordmarkH * (1504 / 357);
  const markSize = size * 1.25;

  const filter =
    tone === 'light' ? 'brightness(0) invert(1)' :
    tone === 'mono' ? 'brightness(0)' :
    'none';

  const mark = (
    <img
      src={`${basePath}logo-mark.png`}
      alt=""
      width={markSize}
      height={markSize}
      style={{ flexShrink: 0, display: 'block', filter }}
    />
  );
  if (variant === 'mark') {
    return <span className={className} style={style}>{mark}</span>;
  }

  const word = (
    <img
      src={`${basePath}logo-kare.png`}
      alt="destekgör"
      width={wordmarkW}
      height={wordmarkH}
      style={{ display: 'block', filter, flexShrink: 0 }}
    />
  );
  if (variant === 'wordmark') {
    return <span className={className} style={style}>{word}</span>;
  }

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: size * 0.4,
        ...style,
      }}
    >
      {mark}
      {word}
    </span>
  );
}
